/**
 * Projects Hook
 *
 * Fetches and manages project data from the API via TanStack Query.
 * Includes retry logic for cases where the server just restarted
 * and projects haven't been synced yet.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Project } from '../types';
import { getProjects } from '../api/client';

const MAX_RETRIES = 3;

export const useProjects = () => {
  const {
    data: projects = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    retry: (failureCount) => failureCount < MAX_RETRIES,
    retryDelay: (attemptIndex) => Math.min(2000 * Math.pow(2, attemptIndex), 10000),
  });

  const projectsMap = useMemo(() => {
    const map: Record<string, Project> = {};
    projects.forEach((project) => {
      map[project.id] = project;
    });
    return map;
  }, [projects]);

  return {
    projects,
    projectsMap,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch projects') : null,
    refetch,
  };
};
